import { useRef, useEffect } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { TrendingUp, Route, Zap, DollarSign, ShieldCheck } from 'lucide-react';

gsap.registerPlugin(ScrollTrigger);

const categories = [
  {
    icon: TrendingUp,
    title: 'Growth Pressure',
    description: 'Population growth, subdivision activity, new school construction, commercial development permits',
    score: 87,
    color: '#7CFF6B',
  },
  {
    icon: Route,
    title: 'Access Strength',
    description: 'Road frontage, nearby highway proximity, ingress/egress quality, future road plans',
    score: 94,
    color: '#22C55E',
  },
  {
    icon: Zap,
    title: 'Utility Potential',
    description: 'Water, sewer, power availability, nearby infrastructure, utility expansion corridors',
    score: 72,
    color: '#2F80ED',
  },
  {
    icon: DollarSign,
    title: 'Deal Viability',
    description: 'Estimated wholesale range, owner history, holding period, comparable sales data',
    score: 68,
    color: '#FFD54A',
  },
  {
    icon: ShieldCheck,
    title: 'Risk Control',
    description: 'Flood zones, wetland buffers, zoning restrictions, land usability concerns',
    score: 15,
    color: '#A855F7',
  },
];

export default function LandSignalScore() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;
    const ctx = gsap.context(() => {
      gsap.fromTo('.score-cat-card', { y: 30, opacity: 0 }, {
        y: 0, opacity: 1, duration: 0.6, stagger: 0.1, ease: 'power3.out',
        scrollTrigger: { trigger: section, start: 'top 75%', once: true },
      });
    }, section);
    return () => ctx.revert();
  }, []);

  return (
    <section id="how-it-works" ref={sectionRef} className="relative py-20" style={{ background: '#07110D' }}>
      <div className="mx-auto max-w-[1100px] px-6">
        <div className="mb-12 text-center">
          <span className="label-accent mb-4 block" style={{ color: '#7CFF6B' }}>SCORING SYSTEM</span>
          <h2 className="font-display text-[clamp(28px,4vw,48px)] font-semibold leading-[1.1] text-[#F7F8F4]">
            PLP Opportunity Score&trade;
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-base" style={{ color: '#a9b4c0' }}>
            Every parcel gets a 0-100 score based on five weighted factors that matter most to land investors. No guesswork. No manual research.
          </p>
        </div>

        <div className="space-y-3">
          {categories.map(({ icon: Icon, title, description, score, color }) => (
            <div key={title} className="score-cat-card flex flex-col gap-3 rounded-2xl border border-white/10 p-4 transition hover:border-white/15 sm:flex-row sm:items-center sm:gap-4" style={{ background: '#101D29' }}>
              <div className="flex items-center gap-3 sm:min-w-0 sm:flex-1">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl" style={{ background: `${color}15` }}>
                  <Icon size={22} style={{ color }} />
                </div>
                <div className="min-w-0">
                  <h3 className="text-sm font-bold text-[#F7F8F4]">{title}</h3>
                  <p className="hidden text-[10px] text-[#8A9BAE] sm:block">— {description}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 sm:shrink-0">
                <div className="h-2 flex-1 overflow-hidden rounded-full bg-white/10 sm:w-24 sm:flex-none">
                  <div className="h-full rounded-full transition-all" style={{ width: `${score}%`, backgroundColor: color }} />
                </div>
                <span className="w-8 text-right font-mono text-sm font-bold" style={{ color }}>{score}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Overall Score Example */}
        <div className="mt-8 rounded-2xl border border-[#7CFF6B]/20 bg-[#7CFF6B]/[0.04] p-6 text-center">
          <p className="text-xs font-black uppercase tracking-[0.2em] text-[#7CFF6B]">Composite Score Example</p>
          <p className="font-display mt-2 text-5xl font-black text-[#7CFF6B]">87<span className="text-2xl text-[#8A9BAE]">/100</span></p>
          <p className="mt-2 text-sm text-[#a9b4c0]">York County, SC · 8.7 acres · Strong Buy</p>
        </div>
      </div>
    </section>
  );
}
